import { Injectable, Injector, NgZone } from '@angular/core';
import {
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpInterceptor,
  HttpErrorResponse
} from '@angular/common/http';
import { Observable, catchError, throwError } from 'rxjs';
import { MatDialog } from '@angular/material/dialog';
import { AccessService } from '../services/access.service';
import { localDB } from '../../shared/helpers/localStorage';
import { SmartAlertService } from 'src/app/shared/components/smart-alert/smart-alert.service';
import { SmartAlertResult } from 'src/app/shared/components/smart-alert/interfaces/custom-dialog.interface';
import { GoogleService } from '../services/googleApi.service';
import { environment as env } from 'src/environments/environment';
import { Router } from '@angular/router';
import { CacheService } from 'src/app/shared/services/cache.service';
import { CookiesService } from '../services/cookies.service';

@Injectable()
export class Interceptor implements HttpInterceptor {
  private _dialog: MatDialog;
  private readonly _injector: Injector;
  private isLoggingOut = false; // Para evitar múltiples llamadas simultáneas

  constructor(
    injector: Injector,
    private readonly _accessService: AccessService,
    private readonly _router: Router,
    private readonly _cacheService: CacheService,
    private readonly _cookiesService: CookiesService,
    private readonly _ngZone: NgZone,
    private readonly _smartAlertService: SmartAlertService,
    private readonly _googleService: GoogleService,
  ) {
    this._injector = injector;
  }

  intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    if (!this._dialog) {
      this._dialog = this._injector.get(MatDialog);
    }

    const token: string = localDB.getItem('token');
    const auth_type: string = localDB.getItem('authType');
    let remoteHost = req.url.split('/')[2];
    let request = this.setRequest(token, auth_type, remoteHost, req);

    return next.handle(request).pipe(
      catchError((err: HttpErrorResponse) => {
        if (err.status == 401) {
          if (isGoogleApi(remoteHost)) {
            this._googleService.updateValueToken('');
            localDB.removeItem('accessToken');
          } else {
            console.warn('Token inválido o expirado. Cerrando sesión...');
            this.cerrarSesion();
          }
        }

        if (err.status == 403) {
          if (isGoogleApi(remoteHost)) {
            this._googleService.updateValueToken('');
            localDB.removeItem('accessToken');
          } else {
            this.swalError403(remoteHost);
          }
        }
        return throwError(() => err);
      })
    );
  }

  setRequest(token: string, authType: string, remoteHost: string, req: HttpRequest<unknown>) {
    let request = req;
    let noUse: boolean = req.headers.has('noUse')
    if (!noUse) {
      if (token) {
        switch (remoteHost) {
          case env.awsBucked:
            request = req.clone({
              setHeaders: {
                Accept: 'image/webp,*/*,application/pdf'
              },
              responseType: 'blob'
            });
            break;
          case 'www.googleapis.com':
          case 'docs.googleapis.com':
            request = req.clone({
              setHeaders: {
                authorization: `Bearer ${localDB.getItem("accessToken")}`,
              }
            });
            break;
          default:
            if (skipInterceptor(remoteHost)) {
              request = req.clone({});
            } else {
              request = req.clone({
                setHeaders: {
                  authorization: `Bearer ${token}`,
                  auth_Type: authType
                }
              });
            }
            break;
        }
      }
    }

    return request;
  }

  private cerrarSesion(): void {
    // Evitar múltiples ejecuciones simultáneas
    if (this.isLoggingOut) {
      return;
    }

    this.isLoggingOut = true;

    this._ngZone.run(async () => {
      try {

        if (this._cookiesService.cookieNameEnv) {
          this._cookiesService.deleteCookie(this._cookiesService.cookieNameEnv);
        }

        localStorage.clear();

        this._cacheService.refreshAllValues().catch(err => 
          console.error('Error limpiando cache:', err)
        );

        window.location.href = '/auth/login';
        
      } catch (error) {
        localStorage.clear();
        window.location.href = '/auth/login';
      }
    });
  }

  swalError403(remoteHost: string) {
    if (remoteHost == env.awsBucked) {
      this._smartAlertService.init({
        type: 'warning',
        title: 'Link de archivo caducado',
        message: 'Por favor actualice.',
        showCancelButton: false
      })
    } else {
      this._smartAlertService.init({
        type: 'info',
        message: 'No Autorizado',
        showCancelButton: false
      }).subscribe((result: SmartAlertResult) => {
        if (result.confirmStatus) {
          this.cerrarSesion();
        }
      })
    }
  }
}

function isGoogleApi(host: string): boolean {
  return host.startsWith('www.googleapis.com') || host.startsWith('docs.googleapis.com');
}

function skipInterceptor(host: string): boolean {
  return host.includes('appsmartbucket');
}